import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {InitializationAndConfigurationService} from './configuration/initializationAndConfiguration.service';
import {map, Observable} from 'rxjs';
import {TicklerService} from './tickler.service';
import {DateHelperService} from './dateHelper.service';
import {UrlService} from './url.service';

@Injectable()
export class ToDoItemService {
    private readonly BASE_URL: string;
    private readonly USER_URL: string;

    private aggCount = 0;
    private initialized = false;
    private ticklerDataInitialized = false;
    private ticklerData = null;

    constructor(private http: HttpClient,
                private initializationAndConfigurationService: InitializationAndConfigurationService,
                private ticklerService: TicklerService,
                private dateHelperService: DateHelperService,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/admin';
        this.USER_URL = this.urlService.getBackendUrl() + 'data/users';
    }

    getToDoItemsByType(type, limit, pageCnt): Observable<any> {
        let params: HttpParams = new HttpParams();
        params = params.set('toDoType', type);
        params = params.set('pageNumber', pageCnt);

        params = params.set('isLimit', limit !== false);

        params = params.set('timestamp', new Date().getTime());

        const requestOptions = {
            params: params
        };

        return this.http.get(this.USER_URL + '/todos', requestOptions);
    }

    getToDoTicklerData(config) {
        let params: HttpParams = new HttpParams();
        // Send one parameter per entry.  The endpoint binds these to a List<String>, which Jersey
        // populates from repeated parameters -- it does not split a single comma-separated value.
        (config.instrumentTypes || []).forEach((instrumentType) => {
            params = params.append('instrumentTypes', instrumentType);
        });
        (config.excludedOuCodes || []).forEach((excludedOuCode) => {
            params = params.append('excludedOuCodes', excludedOuCode);
        });
        params = params.set('timestamp', new Date().getTime());

        const requestOptions = {
            params: params
        };

        return this.http.get(this.USER_URL + '/tickler', requestOptions).pipe(
            map((ticklerData: any) => {
                ticklerData.forEach((ticklerResult) => {
                    if (ticklerResult.placementStartDate != null) {
                        ticklerResult.placementStartDateObj = this.dateHelperService.parseDbDate(ticklerResult.placementStartDate);
                    }
                    if (ticklerResult.placementEndDate != null) {
                        ticklerResult.placementEndDateObj = this.dateHelperService.parseDbDate(ticklerResult.placementEndDate);
                    }
                    if (ticklerResult.episodeStartDate != null) {
                        ticklerResult.episodeStartDateObj = this.dateHelperService.parseDbDate(ticklerResult.episodeStartDate);
                    }
                    if (ticklerResult.episodeAdjustedStartDate != null) {
                        ticklerResult.episodeAdjustedStartDateObj = this.dateHelperService.parseDbDate(ticklerResult.episodeAdjustedStartDate);
                    }
                    if (ticklerResult.episodeEndDate != null) {
                        ticklerResult.episodeEndDateObj = this.dateHelperService.parseDbDate(ticklerResult.episodeEndDate);
                    }
                    if (ticklerResult.episodeAdjustedEndDate != null) {
                        ticklerResult.episodeAdjustedEndDateObj = this.dateHelperService.parseDbDate(ticklerResult.episodeAdjustedEndDate);
                    }
                    if (ticklerResult.clientDob != null) {
                        ticklerResult.clientDobObj = this.dateHelperService.parseDbDate(ticklerResult.clientDob);
                    }
                });

                return ticklerData;
            }));
    }

    calculateAggCount(configList) {
        let aggCount = 0;
        configList.forEach((config) => {
            aggCount += config.count;
        });

        return aggCount;
    }

    initializeToDoConfigurationCounts(toDoConfigRes) {
        toDoConfigRes.forEach((toDoConfig) => {
            toDoConfig.result = [];
            toDoConfig.count = 0;
        });
    }

    getMaxSeverity() {
        let maxSeverity = 0;
        this.initializationAndConfigurationService.getToDoCategoryConfiguration().forEach((config) => {
            if (config.display === true) {
                if (config.count > 0) {
                    if (config.severity > maxSeverity) {
                        maxSeverity = config.severity;
                    }
                }
            }
        });
        return maxSeverity;
    }

    refreshCategory(categoryType) {
        this.initializationAndConfigurationService.getToDoCategoryConfiguration().forEach((config) => {
            if (config.toDoType === categoryType) {
                this.getToDoItemsByType(config.toDoType,
                    (config.originalCount <
                        (this.initializationAndConfigurationService.getApplicationConfiguration().inboxItemsLimit * config.pageCnt)) ? false : true /*false*/,
                    config.pageCnt + 1)
                    .subscribe({
                            next: (res: any) => {
                                config.result = res.itemList;
                                config.count = res.itemList.length;
                                config.originalCount = res.count;
                                config.loaded = true;
                                if (config.originalCount >= (this.initializationAndConfigurationService.getApplicationConfiguration().inboxItemsLimit * config.pageCnt)) {
                                    config.pageCnt = config.pageCnt + 1;
                                }
                            },
                            error: (err) => {
                            }
                        }
                    );
            }
        });
    }

    getAggregateCount(callback) {
        const toDoConfigRes = this.initializationAndConfigurationService.getToDoCategoryConfiguration()
        this.initializeToDoConfigurationCounts(toDoConfigRes);

        // toDoConfigRes is the local copies of how each of the ToDo categories should be displayed.
        // Each element in toDoConfigRes will store the count and list of each of the items included in it.
        // For example, a toDoConfigRes config will be the Initial Upcoming, and will have a count of how
        // many along with the information about each of the Initial Upcoming -- client name, dates, etc.
        const appCfg = this.initializationAndConfigurationService.getApplicationConfiguration();

        // The toDoCommon is the *full* configuration for this instance.  It will indicate
        // which categories should be displayed, how to parse the assessment data to count whether
        // things belong in certain categories, etc.
        const toDoCommon = JSON.parse(appCfg.toDoCommon);
        if (toDoCommon) {
            toDoCommon.configs.forEach((toDoCommonConfig) => {
                this.getToDoTicklerData(toDoCommonConfig).subscribe((ticklerRes) => {
                    toDoConfigRes.forEach((toDoConfig) => {
                        if ((toDoConfig.display === true) && (toDoConfig.dataSource === 'Tickler')) {
                            this.ticklerService.hydrateTicklerConfig(toDoConfig, toDoCommonConfig, ticklerRes);
                            callback(this.calculateAggCount(toDoConfigRes));
                        }
                    });
                });
            });
        }

        toDoConfigRes.forEach((config) => {
            if ((config.display === true) && (config.dataSource === 'Inbox')) {
                if (config.loaded === false) {
                    this.getToDoItemsByType(config.toDoType, true, config.pageCnt).subscribe((res) => {
                        config.result = res.itemList;
                        config.count = res.itemList.length;
                        config.originalCount = res.count;
                        config.loaded = true;
                        callback(this.calculateAggCount(toDoConfigRes));
                    });
                } else {
                    callback(this.calculateAggCount(toDoConfigRes));
                }
            }
        });
    }

    getFirstActiveCategory() {
        const toDoConfigRes = this.initializationAndConfigurationService.getToDoCategoryConfiguration()

        toDoConfigRes.forEach((toDoConfig) => {
            if ((toDoConfig.display === true)) {
                return toDoConfig;
            }
        });
    }
}
